import { IconButton } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';

import {
  FormProvider,
  Iconify,
  MenuPopover,
  RowActions as MenuRowActions,
  useMenuPopover,
} from '@app/components';
import { useDialog, useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { IAddCandidateSchema } from '../types';
import { AddCandidatePage } from './add-candidate-page';
import { ConfirmationDialog } from '@app/components/dialog';
import { updateCandidateStatusSchema } from '../validators';
import { updateCandidateStatus } from '../slice';
import { selectUpdateCandidateStatusState } from '../selectors';
interface Props {
  row: IAddCandidateSchema;
}

const RowActions = ({ row }: Props) => {
  const menuPopover = useMenuPopover();
  const editDialog = useDialog();
  const confirmDialog = useDialog();
  const methods = useForm({
    resolver: yupResolver(updateCandidateStatusSchema) as any,
  });

  const actions =
    row.status === 'Saved'
      ? [
          { label: Strings.rowActions.edit, callback: editDialog.show },
          {
            label: Strings.rowActions.rejected,
            callback: () => {
              confirmDialog.show();
              methods.setValue('reason', '');
            },
          },
        ]
      : [
          { label: Strings.rowActions.edit, callback: editDialog.show },
          {
            label:
              row.status === Strings.status.shortlisted || row.status === 'Active'
                ? Strings.rowActions.rejected
                : Strings.status.shortlisted,
            callback: () => {
              confirmDialog.show();
              methods.setValue('reason', '');
            },
          },
        ];
  const { loading } = useRequestState({
    stateSelector: selectUpdateCandidateStatusState,
    successMessageShown: true,
    onSuccess: confirmDialog.hide,
    errorShown: true,
  });
  const dispatch = useDispatch();
  const newStatus =
    row.status === Strings.status.shortlisted || row.status === Strings.status.active
      ? Strings.rowActions.rejected
      : Strings.status.shortlisted;
  const updateCandidateStatusHandle = (reason: string) => {
    dispatch(updateCandidateStatus({ ...row, id: row?.id, reason, status: newStatus }));
  };

  return (
    <>
      <IconButton
        onClick={row.status === 'InterviewScheduled' ? undefined : menuPopover.show}
        sx={{ color: row.status === 'InterviewScheduled' ? 'lightgrey' : 'default' }}
      >
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton>

      <MenuPopover
        open={menuPopover.rowMenuAnchorElement}
        arrow="right-top"
        sx={{ width: 180 }}
        onClose={menuPopover.hide}
      >
        <MenuRowActions actions={actions} onClickAction={menuPopover.hide} />
      </MenuPopover>
      {editDialog.visible && <AddCandidatePage open onClose={editDialog.hide} row={row} />}
      <FormProvider methods={methods}>
        <ConfirmationDialog
          title={`${Strings.common.update} ${Strings.field.status}`}
          confirmationButtonTitle={Strings.button.save}
          caption={Strings.common.updateCaption(newStatus)}
          visible={confirmDialog.visible}
          loading={loading}
          onClickConfirm={updateCandidateStatusHandle}
          onClose={confirmDialog.hide}
        />
      </FormProvider>
    </>
  );
};

export { RowActions };
